import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  real,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { organizations } from './organizations';
import { users } from './users';

export interface ChatAttachment {
  fileName: string;
  fileUrl: string;
  storageKey: string;
  mimeType: string;
  size: number;
}

// Google Chat Integration Tables
export const chatSpaces = pgTable("chat_spaces", {
  id: varchar("id").primaryKey(), // Google Chat space ID
  displayName: varchar("display_name").notNull(),
  spaceType: varchar("space_type").default("SPACE"), // SPACE, DIRECT_MESSAGE, GROUP_CONVERSATION
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }), // Optional: link to claim
  isActive: boolean("is_active").default(true),
  metadata: jsonb("metadata"), // Additional space metadata
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


export const chatMessages = pgTable("chat_messages", {
  id: varchar("id").primaryKey(), // Google Chat message ID
  spaceId: varchar("space_id").notNull().references(() => chatSpaces.id, { onDelete: "cascade" }),
  senderId: varchar("sender_id").notNull(), // User ID who sent the message
  senderEmail: varchar("sender_email"),
  text: text("text"),
  compactedText: text("compacted_text"),
  threadId: varchar("thread_id"), // For threaded conversations
  attachments: jsonb("attachments").$type<ChatAttachment[]>(), // Array of attachment metadata
  mentions: jsonb("mentions").$type<Array<{ userId?: string; claimId?: string; offset: number; length: number }>>(),
  messageType: varchar("message_type").default("text"), // 'text' | 'action_confirmation' | 'memory_cleared' | 'system_error'
  metadata: jsonb("metadata"),
  editedAt: timestamp("edited_at"),
  deletedAt: timestamp("deleted_at"),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
});


// Chat Agents table — configurable AI agents for multi-agent chat
export const chatAgents = pgTable("chat_agents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: varchar("slug").notNull(),
  displayName: varchar("display_name").notNull(),
  description: text("description"),
  model: varchar("model").notNull().default("gemini-2.5-flash"),
  systemInstructions: text("system_instructions").notNull(),
  personality: text("personality"),
  avatarUrl: varchar("avatar_url"),
  allowedTools: jsonb("allowed_tools").default([]),
  temperature: real("temperature").default(0.7),
  maxTokens: integer("max_tokens").default(500),
  contextWindowSize: integer("context_window_size").default(20),
  isActive: boolean("is_active").default(true),
  canReadClaims: boolean("can_read_claims").default(false),
  canReadTasks: boolean("can_read_tasks").default(false),
  canReadCommunications: boolean("can_read_communications").default(false),
  canReadDocuments: boolean("can_read_documents").default(false),
  canReadTimeline: boolean("can_read_timeline").default(false),
  canWriteClaims: boolean("can_write_claims").default(false),
  canWriteTasks: boolean("can_write_tasks").default(false),
  canWriteCommunications: boolean("can_write_communications").default(false),
  canWriteTimeline: boolean("can_write_timeline").default(false),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


export const chatParticipants = pgTable("chat_participants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spaceId: varchar("space_id").notNull().references(() => chatSpaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  agentId: uuid("agent_id").references(() => chatAgents.id, { onDelete: "cascade" }),
  role: varchar("role").default("member"), // member, manager, owner
  joinedAt: timestamp("joined_at").defaultNow(),
  lastReadAt: timestamp("last_read_at"),
  hiddenAt: timestamp("hidden_at"),
}, (table) => [
  index("idx_chat_participants_space").on(table.spaceId),
  index("idx_chat_participants_user").on(table.userId),
]);


// Pending Tool Calls — agent write actions awaiting user approval
export const pendingToolCalls = pgTable("pending_tool_calls", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  spaceId: varchar("space_id").notNull().references(() => chatSpaces.id, { onDelete: "cascade" }),
  messageId: varchar("message_id").notNull().references(() => chatMessages.id, { onDelete: "cascade" }),
  agentId: varchar("agent_id").notNull(),
  toolName: varchar("tool_name").notNull(),
  toolArgs: jsonb("tool_args").notNull(),
  summary: text("summary").notNull(),
  details: jsonb("details"),
  status: varchar("status").notNull().default("pending"), // pending | approved | declined
  resolvedBy: varchar("resolved_by").references(() => users.id),
  resolvedAt: timestamp("resolved_at"),
  toolResult: jsonb("tool_result"),
  aiContext: jsonb("ai_context"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const chatMessageReactions = pgTable("chat_message_reactions", {
  messageId: varchar("message_id").notNull().references(() => chatMessages.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  emoji: varchar("emoji", { length: 64 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  primaryKey({ columns: [table.messageId, table.userId, table.emoji] }),
]);

export interface ChatReactionSummary {
  emoji: string;
  count: number;
  userIds: string[];
}

export const chatDigestTracking = pgTable("chat_digest_tracking", {
  userId: varchar("user_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  lastDigestSentAt: timestamp("last_digest_sent_at"),
  updatedAt: timestamp("updated_at").defaultNow(),
});
